/**
 * WebhookTutorialModal - Modal de documentação completa para integração de webhook
 * 
 * Fornece guia detalhado com:
 * - Visão geral do funcionamento
 * - Mapeamento de campos
 * - Controle de duplicidade
 * - Autenticação e segurança
 * - Limites de requisição
 * - Exemplos de código (cURL, PHP, Python, JavaScript)
 * 
 * @author Sistema CRM UaizyChat
 * @version 1.0.0
 */

import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import {
  BookOpen,
  MapPin,
  Copy,
  Check,
  FileCode,
  Shield,
  Clock,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  ExternalLink,
  ChevronRight,
  Zap,
  RefreshCw,
  Layers,
  Key,
  Timer,
  Code,
  Terminal,
} from 'lucide-react';

// ============================================================================
// TIPOS
// ============================================================================

interface WebhookTutorialModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  webhookUrl: string;
  webhookToken: string;
  authHeaderName?: string;
  authHeaderValue?: string;
}

// ============================================================================
// COMPONENTES AUXILIARES
// ============================================================================

/**
 * Bloco de código com sintaxe destacada e botão de copiar
 */
function CodeBlock({ 
  language, 
  code, 
  title 
}: { 
  language: string;
  code: string;
  title?: string;
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    toast.success('Código copiado!');
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="rounded-lg border bg-card overflow-hidden">
      {/* Header do bloco */}
      <div className="flex items-center justify-between px-4 py-2 bg-muted/50 border-b">
        <div className="flex items-center gap-2">
          <Terminal className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">{title || language}</span>
          <Badge variant="outline" className="text-xs">{language}</Badge>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleCopy}
          className="h-7 px-2"
        >
          {copied ? (
            <Check className="h-3.5 w-3.5 text-green-500" />
          ) : (
            <Copy className="h-3.5 w-3.5" />
          )}
        </Button>
      </div>
      {/* Código */}
      <pre className="p-4 overflow-x-auto text-sm">
        <code className="text-foreground whitespace-pre">{code}</code>
      </pre>
    </div>
  );
}

/**
 * Linha de campo com descrição e badge de obrigatoriedade
 */
function FieldRow({ 
  fieldName, 
  description, 
  required = false,
  example
}: { 
  fieldName: string;
  description: string;
  required?: boolean;
  example?: string;
}) {
  return (
    <div className="flex items-start gap-3 py-3 border-b last:border-0">
      <code className="px-2 py-0.5 rounded bg-muted text-sm font-mono shrink-0">
        {fieldName}
      </code>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-foreground">{description}</p>
        {example && (
          <p className="text-xs text-muted-foreground mt-1">
            Exemplo: <code className="bg-muted px-1 rounded">{example}</code>
          </p>
        )}
      </div>
      <Badge variant={required ? 'default' : 'secondary'} className="shrink-0">
        {required ? 'Obrigatório' : 'Opcional'}
      </Badge>
    </div>
  );
}

/**
 * Card de limite de requisição
 */
function RateLimitCard({ 
  type, 
  limit, 
  window,
  icon: Icon,
  description
}: { 
  type: string;
  limit: number;
  window: string;
  icon: React.ComponentType<{ className?: string }>;
  description: string;
}) {
  return (
    <div className="p-4 rounded-lg border bg-card">
      <div className="flex items-center gap-3 mb-2">
        <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
          <Icon className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h4 className="font-semibold">{type}</h4>
          <p className="text-sm text-muted-foreground">
            <span className="font-mono text-primary">{limit}</span> requisições / {window}
          </p>
        </div>
      </div>
      <p className="text-sm text-muted-foreground">{description}</p>
    </div>
  );
}

/**
 * Card de comportamento (permitido/bloqueado)
 */
function BehaviorCard({ 
  allowed, 
  title, 
  description 
}: { 
  allowed: boolean;
  title: string;
  description: string;
}) {
  return (
    <div className={cn(
      "p-4 rounded-lg border-2 transition-all",
      allowed 
        ? "border-green-500/50 bg-green-500/5" 
        : "border-red-500/50 bg-red-500/5"
    )}>
      <div className="flex items-center gap-2 mb-2">
        {allowed ? (
          <CheckCircle2 className="h-5 w-5 text-green-500" />
        ) : (
          <XCircle className="h-5 w-5 text-red-500" />
        )}
        <span className="font-semibold">{title}</span>
      </div>
      <p className="text-sm text-muted-foreground">{description}</p>
    </div>
  );
}

/**
 * Seção com título e ícone
 */
function Section({ 
  icon: Icon, 
  title, 
  children 
}: { 
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-3">
      <h3 className="font-semibold flex items-center gap-2">
        <Icon className="h-4 w-4 text-primary" />
        {title}
      </h3>
      {children}
    </div>
  );
}

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

export function WebhookTutorialModal({
  open,
  onOpenChange,
  webhookUrl,
  webhookToken,
  authHeaderName,
  authHeaderValue,
}: WebhookTutorialModalProps) {
  const [activeTab, setActiveTab] = useState('overview');
  const [urlCopied, setUrlCopied] = useState(false);

  // Handler para copiar URL
  const handleCopyUrl = async () => {
    await navigator.clipboard.writeText(webhookUrl);
    setUrlCopied(true);
    toast.success('URL copiada!');
    setTimeout(() => setUrlCopied(false), 2000);
  };

  // Gera exemplos de código com a URL e token atuais
  const curlExample = `curl -X POST "${webhookUrl}" \\
  -H "Content-Type: application/json" \\${authHeaderValue ? `
  -H "${authHeaderName || 'X-Webhook-Secret'}: ${authHeaderValue}" \\` : ''}
  -d '{
    "nome": "João Silva",
    "telefone": "5511999999999",
    "email": "joao@email.com",
    "titulo": "Interesse em Produto X",
    "descricao": "Lead captado via formulário"
  }'`;

  const phpExample = `<?php
$url = "${webhookUrl}";

$data = [
    "nome" => "João Silva",
    "telefone" => "5511999999999",
    "email" => "joao@email.com",
    "titulo" => "Interesse em Produto X",
    "descricao" => "Lead captado via formulário"
];

$headers = [
    "Content-Type: application/json",${authHeaderValue ? `
    "${authHeaderName || 'X-Webhook-Secret'}: ${authHeaderValue}",` : ''}
];

$ch = curl_init($url);
curl_setopt($ch, CURLOPT_POST, true);
curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode($data));
curl_setopt($ch, CURLOPT_HTTPHEADER, $headers);
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);

$response = curl_exec($ch);
$httpCode = curl_getinfo($ch, CURLINFO_HTTP_CODE);
curl_close($ch);

echo "Status: $httpCode\\n";
echo "Response: $response\\n";`;

  const pythonExample = `import requests

url = "${webhookUrl}"

payload = {
    "nome": "João Silva",
    "telefone": "5511999999999",
    "email": "joao@email.com",
    "titulo": "Interesse em Produto X",
    "descricao": "Lead captado via formulário"
}

headers = {
    "Content-Type": "application/json",${authHeaderValue ? `
    "${authHeaderName || 'X-Webhook-Secret'}": "${authHeaderValue}",` : ''}
}

response = requests.post(url, json=payload, headers=headers)

print(f"Status: {response.status_code}")
print(f"Response: {response.json()}")`;

  const jsExample = `const url = "${webhookUrl}";

const payload = {
  nome: "João Silva",
  telefone: "5511999999999",
  email: "joao@email.com",
  titulo: "Interesse em Produto X",
  descricao: "Lead captado via formulário"
};

const response = await fetch(url, {
  method: "POST",
  headers: {
    "Content-Type": "application/json",${authHeaderValue ? `
    "${authHeaderName || 'X-Webhook-Secret'}": "${authHeaderValue}",` : ''}
  },
  body: JSON.stringify(payload)
});

const data = await response.json();
console.log("Status:", response.status);
console.log("Response:", data);`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl h-[85vh] flex flex-col p-0 gap-0">
        {/* Header fixo */}
        <DialogHeader className="px-6 py-4 border-b shrink-0">
          <DialogTitle className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
              <BookOpen className="h-5 w-5 text-primary" />
            </div>
            <div>
              <span className="text-xl">Guia de Integração</span>
              <Badge variant="secondary" className="ml-2">Webhook CRM</Badge>
            </div>
          </DialogTitle>
        </DialogHeader>

        {/* Tabs de navegação */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0">
          <div className="px-6 pt-4 shrink-0">
            <TabsList className="grid w-full grid-cols-6 h-auto">
              <TabsTrigger value="overview" className="flex flex-col gap-1 py-2 h-auto">
                <BookOpen className="h-4 w-4" />
                <span className="text-xs">Visão Geral</span>
              </TabsTrigger>
              <TabsTrigger value="mapping" className="flex flex-col gap-1 py-2 h-auto">
                <MapPin className="h-4 w-4" />
                <span className="text-xs">Mapeamento</span>
              </TabsTrigger>
              <TabsTrigger value="duplicate" className="flex flex-col gap-1 py-2 h-auto">
                <Layers className="h-4 w-4" />
                <span className="text-xs">Duplicidade</span>
              </TabsTrigger>
              <TabsTrigger value="security" className="flex flex-col gap-1 py-2 h-auto">
                <Shield className="h-4 w-4" />
                <span className="text-xs">Segurança</span>
              </TabsTrigger>
              <TabsTrigger value="limits" className="flex flex-col gap-1 py-2 h-auto">
                <Clock className="h-4 w-4" />
                <span className="text-xs">Limites</span>
              </TabsTrigger>
              <TabsTrigger value="examples" className="flex flex-col gap-1 py-2 h-auto">
                <Code className="h-4 w-4" />
                <span className="text-xs">Exemplos</span>
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Conteúdo com scroll */}
          <ScrollArea className="flex-1 min-h-0">
            <div className="px-6 py-4">
              {/* ============================================================= */}
              {/* TAB: VISÃO GERAL */}
              {/* ============================================================= */}
              <TabsContent value="overview" className="mt-0 space-y-6">
                {/* Endpoint */}
                <Section icon={Zap} title="Endpoint do Webhook">
                  <div className="flex gap-2">
                    <code className="flex-1 px-3 py-2 rounded-lg bg-muted text-sm font-mono break-all">
                      {webhookUrl || 'URL será exibida após salvar o webhook'}
                    </code>
                    {webhookUrl && (
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={handleCopyUrl}
                        className="shrink-0"
                      >
                        {urlCopied ? (
                          <Check className="h-4 w-4 text-green-500" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </Button>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Envie requisições <Badge variant="outline">POST</Badge> para esta URL com os dados do lead em formato JSON.
                  </p>
                </Section>

                <Separator />

                {/* O que é */}
                <Section icon={BookOpen} title="O que é o Webhook de Integração?">
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    O webhook é um endpoint HTTP que permite que sistemas externos enviem dados automaticamente 
                    para criar negócios no seu CRM. Quando uma requisição válida é recebida, o sistema cria 
                    automaticamente um contato (se não existir) e um negócio na fase configurada do pipeline.
                  </p>
                </Section>

                {/* Fluxo */}
                <Section icon={RefreshCw} title="Como Funciona">
                  <div className="space-y-3 p-4 rounded-lg border bg-muted/30">
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-bold text-primary">
                        1
                      </div>
                      <p className="text-sm">Sistema externo envia <strong>POST</strong> com dados do lead</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-bold text-primary">
                        2
                      </div>
                      <p className="text-sm">Webhook valida token, rate limit e autenticação (se configurada)</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-bold text-primary">
                        3
                      </div>
                      <p className="text-sm">Sistema verifica duplicidade (se habilitada)</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-bold text-primary">
                        4
                      </div>
                      <p className="text-sm">Cria ou atualiza contato e negócio no pipeline</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-bold text-primary">
                        5
                      </div>
                      <p className="text-sm">Retorna resposta JSON com status e IDs criados</p>
                    </div>
                  </div>
                </Section>

                {/* Casos de uso */}
                <Section icon={ExternalLink} title="Casos de Uso Comuns">
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div className="p-3 rounded-lg border bg-card">
                      <h4 className="font-medium text-sm mb-1">Landing Pages</h4>
                      <p className="text-xs text-muted-foreground">
                        Formulários de captação que enviam leads automaticamente
                      </p>
                    </div>
                    <div className="p-3 rounded-lg border bg-card">
                      <h4 className="font-medium text-sm mb-1">ERPs e Sistemas Internos</h4>
                      <p className="text-xs text-muted-foreground">
                        Integração com sistemas de gestão existentes
                      </p>
                    </div>
                    <div className="p-3 rounded-lg border bg-card">
                      <h4 className="font-medium text-sm mb-1">Automações de Marketing</h4>
                      <p className="text-xs text-muted-foreground">
                        RD Station, Hubspot, Mailchimp e outros
                      </p>
                    </div>
                    <div className="p-3 rounded-lg border bg-card">
                      <h4 className="font-medium text-sm mb-1">Chatbots e Bots</h4>
                      <p className="text-xs text-muted-foreground">
                        WhatsApp, Telegram, Instagram Direct
                      </p>
                    </div>
                  </div>
                </Section>

                {/* Navegação */}
                <div className="flex justify-end pt-4">
                  <Button onClick={() => setActiveTab('mapping')} variant="outline">
                    Próximo: Mapeamento de Campos
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </TabsContent>

              {/* ============================================================= */}
              {/* TAB: MAPEAMENTO */}
              {/* ============================================================= */}
              <TabsContent value="mapping" className="mt-0 space-y-6">
                <p className="text-sm text-muted-foreground">
                  Configure quais campos do seu payload JSON correspondem aos campos do sistema.
                  Você pode usar <strong>notação de ponto</strong> para acessar campos aninhados.
                </p>

                {/* Campos disponíveis */}
                <Section icon={MapPin} title="Campos do Sistema">
                  <div className="rounded-lg border divide-y">
                    <FieldRow
                      fieldName="telefone"
                      description="Número de telefone do contato. Formato recomendado: código do país + DDD + número"
                      required={true}
                      example="5511999999999"
                    />
                    <FieldRow
                      fieldName="nome"
                      description="Nome completo do contato. Se não informado, será gerado a partir do telefone"
                      example="João Silva"
                    />
                    <FieldRow
                      fieldName="email"
                      description="E-mail do contato para comunicação adicional"
                      example="joao@empresa.com"
                    />
                    <FieldRow
                      fieldName="titulo"
                      description="Título do negócio a ser criado. Se não informado, usa um título padrão"
                      example="Interesse em Consultoria"
                    />
                    <FieldRow
                      fieldName="descricao"
                      description="Descrição detalhada do negócio ou anotações adicionais"
                      example="Lead captado via formulário da página de contato"
                    />
                  </div>
                </Section>

                <Separator />

                {/* Notação de ponto */}
                <Section icon={FileCode} title="Notação de Ponto">
                  <p className="text-sm text-muted-foreground mb-3">
                    Se o seu sistema envia dados em estrutura aninhada, configure o mapeamento usando pontos.
                  </p>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs font-medium mb-2 text-muted-foreground">Payload recebido:</p>
                      <CodeBlock
                        language="json"
                        code={`{
  "lead": {
    "info": {
      "name": "Maria Santos",
      "phone": "5521988887777"
    },
    "email": "maria@email.com"
  }
}`}
                      />
                    </div>
                    <div>
                      <p className="text-xs font-medium mb-2 text-muted-foreground">Mapeamento:</p>
                      <div className="space-y-2 p-3 rounded-lg border bg-muted/30">
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Nome:</span>
                          <code className="bg-muted px-2 py-0.5 rounded">lead.info.name</code>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Telefone:</span>
                          <code className="bg-muted px-2 py-0.5 rounded">lead.info.phone</code>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">E-mail:</span>
                          <code className="bg-muted px-2 py-0.5 rounded">lead.email</code>
                        </div>
                      </div>
                    </div>
                  </div>
                </Section>

                {/* Navegação */}
                <div className="flex justify-between pt-4">
                  <Button onClick={() => setActiveTab('overview')} variant="ghost">
                    Voltar
                  </Button>
                  <Button onClick={() => setActiveTab('duplicate')} variant="outline">
                    Próximo: Controle de Duplicidade
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </TabsContent>

              {/* ============================================================= */}
              {/* TAB: DUPLICIDADE */}
              {/* ============================================================= */}
              <TabsContent value="duplicate" className="mt-0 space-y-6">
                <p className="text-sm text-muted-foreground">
                  Configure como o sistema deve tratar requisições repetidas para o mesmo telefone.
                </p>

                {/* Escopo */}
                <Section icon={Layers} title="Escopo de Verificação">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="p-4 rounded-lg border bg-card">
                      <h4 className="font-semibold text-sm mb-2 flex items-center gap-2">
                        <Badge variant="outline">Pipeline</Badge>
                      </h4>
                      <p className="text-sm text-muted-foreground">
                        Verifica duplicidade apenas dentro do pipeline configurado. O mesmo contato 
                        pode ter negócios em pipelines diferentes.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg border bg-card">
                      <h4 className="font-semibold text-sm mb-2 flex items-center gap-2">
                        <Badge variant="outline">Workspace</Badge>
                      </h4>
                      <p className="text-sm text-muted-foreground">
                        Verifica duplicidade em todos os pipelines do workspace. Mais restritivo, 
                        evita qualquer duplicação.
                      </p>
                    </div>
                  </div>
                </Section>

                <Separator />

                {/* Janela de tempo */}
                <Section icon={Timer} title="Janela de Tempo">
                  <p className="text-sm text-muted-foreground mb-3">
                    Define o período para considerar um contato como duplicado. Exemplos:
                  </p>
                  <div className="space-y-2 p-4 rounded-lg border bg-muted/30">
                    <div className="flex items-center gap-3 text-sm">
                      <Badge variant="secondary">1 hora</Badge>
                      <span>Proteção contra cliques acidentais em formulários</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm">
                      <Badge variant="secondary">7 dias</Badge>
                      <span>Padrão recomendado para maioria dos casos</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm">
                      <Badge variant="secondary">30 dias</Badge>
                      <span>Ciclos de venda mais longos</span>
                    </div>
                  </div>
                </Section>

                <Separator />

                {/* Comportamentos */}
                <Section icon={RefreshCw} title="Comportamento ao Detectar Duplicidade">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <BehaviorCard
                      allowed={true}
                      title="Ignorar (Skip)"
                      description="A requisição é descartada silenciosamente. O negócio existente permanece inalterado. Ideal para evitar ruído."
                    />
                    <BehaviorCard
                      allowed={true}
                      title="Atualizar (Update)"
                      description="Atualiza os dados do negócio existente com as novas informações. Útil para enriquecer dados."
                    />
                  </div>
                </Section>

                {/* Navegação */}
                <div className="flex justify-between pt-4">
                  <Button onClick={() => setActiveTab('mapping')} variant="ghost">
                    Voltar
                  </Button>
                  <Button onClick={() => setActiveTab('security')} variant="outline">
                    Próximo: Segurança
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </TabsContent>

              {/* ============================================================= */}
              {/* TAB: SEGURANÇA */}
              {/* ============================================================= */}
              <TabsContent value="security" className="mt-0 space-y-6">
                <p className="text-sm text-muted-foreground">
                  Configure autenticação adicional para garantir que apenas sistemas autorizados 
                  possam enviar dados para o webhook.
                </p>

                {/* Autenticação por header */}
                <Section icon={Key} title="Autenticação por Header Secreto">
                  <p className="text-sm text-muted-foreground mb-3">
                    Quando habilitada, o sistema valida se a requisição contém um header específico 
                    com o valor secreto configurado.
                  </p>
                  
                  <CodeBlock
                    language="http"
                    title="Exemplo de requisição autenticada"
                    code={`POST ${webhookUrl || '/webhook'}
Content-Type: application/json
${authHeaderName || 'X-Webhook-Secret'}: ${authHeaderValue || 'seu-valor-secreto-aqui'}

{"telefone": "5511999999999", "nome": "Lead"}`}
                  />
                </Section>

                <Separator />

                {/* Boas práticas */}
                <Section icon={Shield} title="Boas Práticas de Segurança">
                  <div className="space-y-3">
                    <BehaviorCard
                      allowed={true}
                      title="Sempre use HTTPS"
                      description="O endpoint já está configurado com SSL/TLS. Nunca envie dados sensíveis sem criptografia."
                    />
                    <BehaviorCard
                      allowed={true}
                      title="Mantenha o token privado"
                      description="Não exponha a URL do webhook em código front-end ou repositórios públicos."
                    />
                    <BehaviorCard
                      allowed={true}
                      title="Regenere tokens periodicamente"
                      description="Se suspeitar de vazamento, regenere o token imediatamente."
                    />
                    <BehaviorCard
                      allowed={false}
                      title="Não compartilhe publicamente"
                      description="Evite postar URLs de webhook em fóruns, issues ou documentação pública."
                    />
                  </div>
                </Section>

                {/* Navegação */}
                <div className="flex justify-between pt-4">
                  <Button onClick={() => setActiveTab('duplicate')} variant="ghost">
                    Voltar
                  </Button>
                  <Button onClick={() => setActiveTab('limits')} variant="outline">
                    Próximo: Limites
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </TabsContent>

              {/* ============================================================= */}
              {/* TAB: LIMITES */}
              {/* ============================================================= */}
              <TabsContent value="limits" className="mt-0 space-y-6">
                {/* Alerta */}
                <div className="flex items-start gap-3 p-4 rounded-lg bg-amber-500/10 border border-amber-500/30">
                  <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-amber-700 dark:text-amber-400 mb-1">
                      Rate Limiting Ativo
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      Para proteger o sistema contra abusos, há limites de requisições por minuto. 
                      Requisições que excedem o limite recebem erro <code className="bg-muted px-1 rounded">429</code>.
                    </p>
                  </div>
                </div>

                {/* Limites */}
                <Section icon={Timer} title="Limites de Requisição">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <RateLimitCard
                      type="Por Token"
                      limit={60}
                      window="minuto"
                      icon={Key}
                      description="Cada webhook pode receber até 60 requisições por minuto, independente do IP de origem."
                    />
                    <RateLimitCard
                      type="Por IP"
                      limit={30}
                      window="minuto"
                      icon={Shield}
                      description="Cada endereço IP pode enviar até 30 requisições por minuto para qualquer webhook."
                    />
                  </div>
                </Section>

                <Separator />

                {/* Códigos de erro */}
                <Section icon={AlertTriangle} title="Códigos de Resposta">
                  <div className="rounded-lg border divide-y">
                    <div className="flex items-center gap-4 p-3">
                      <Badge className="bg-green-500/10 text-green-600 border-green-500/30">200</Badge>
                      <div>
                        <p className="font-medium text-sm">Sucesso</p>
                        <p className="text-xs text-muted-foreground">Negócio criado ou atualizado com sucesso</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 p-3">
                      <Badge className="bg-amber-500/10 text-amber-600 border-amber-500/30">400</Badge>
                      <div>
                        <p className="font-medium text-sm">Requisição Inválida</p>
                        <p className="text-xs text-muted-foreground">Payload malformado ou campo obrigatório ausente</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 p-3">
                      <Badge className="bg-red-500/10 text-red-600 border-red-500/30">401</Badge>
                      <div>
                        <p className="font-medium text-sm">Não Autorizado</p>
                        <p className="text-xs text-muted-foreground">Token inválido ou header de autenticação incorreto</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 p-3">
                      <Badge className="bg-red-500/10 text-red-600 border-red-500/30">429</Badge>
                      <div>
                        <p className="font-medium text-sm">Limite Excedido</p>
                        <p className="text-xs text-muted-foreground">Rate limit atingido. Aguarde antes de tentar novamente</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 p-3">
                      <Badge className="bg-red-500/10 text-red-600 border-red-500/30">500</Badge>
                      <div>
                        <p className="font-medium text-sm">Erro Interno</p>
                        <p className="text-xs text-muted-foreground">Erro no servidor. Contate o suporte se persistir</p>
                      </div>
                    </div>
                  </div>
                </Section>

                {/* Navegação */}
                <div className="flex justify-between pt-4">
                  <Button onClick={() => setActiveTab('security')} variant="ghost">
                    Voltar
                  </Button>
                  <Button onClick={() => setActiveTab('examples')} variant="outline">
                    Próximo: Exemplos de Código
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </TabsContent>

              {/* ============================================================= */}
              {/* TAB: EXEMPLOS */}
              {/* ============================================================= */}
              <TabsContent value="examples" className="mt-0 space-y-6">
                <p className="text-sm text-muted-foreground">
                  Copie e adapte os exemplos abaixo para integrar seu sistema com o webhook.
                  {authHeaderValue && (
                    <span className="block mt-1">
                      <Badge variant="outline" className="mr-1">Autenticação</Badge>
                      Os exemplos já incluem o header de autenticação configurado.
                    </span>
                  )}
                </p>

                {/* cURL */}
                <Section icon={Terminal} title="cURL (Linha de Comando)">
                  <CodeBlock language="bash" code={curlExample} title="Terminal" />
                </Section>

                <Separator />

                {/* PHP */}
                <Section icon={Code} title="PHP">
                  <CodeBlock language="php" code={phpExample} title="webhook.php" />
                </Section>

                <Separator />

                {/* Python */}
                <Section icon={Code} title="Python">
                  <CodeBlock language="python" code={pythonExample} title="webhook.py" />
                </Section>

                <Separator />

                {/* JavaScript */}
                <Section icon={Code} title="JavaScript / Node.js">
                  <CodeBlock language="javascript" code={jsExample} title="webhook.js" />
                </Section>

                {/* Navegação */}
                <div className="flex justify-between pt-4">
                  <Button onClick={() => setActiveTab('limits')} variant="ghost">
                    Voltar
                  </Button>
                  <Button onClick={() => onOpenChange(false)}>
                    Entendido
                  </Button>
                </div>
              </TabsContent>
            </div>
          </ScrollArea>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
